
import StringUtils from '@/utils/StringUtils.js';
import RcDomainObject from "../../../../common/RcDomainObject";
import Company from "../../../company/Company.js";
import NoteMap from "@/domain/model/schedule/question/option/note/NoteMap.js";

export default class CheckListItemOption extends RcDomainObject {
  
  static MODEL_NAME = 'CheckListItemOption';
  
  static Fields = {
      Index: "index",
      Name: "name",
      Company: "Company",
      Status: "status",
      Category: "category",
      // Notes describing the answer: the schedule's own field, a map of
      // VehicleScheduleQuestionNote records {position, value}.
      NoteMap: NoteMap.MODEL_NAME,
      Meaning: "meaning",
      Kind: "kind",
      Position: "position",
  }
  
  static Values = {
    Category: {
      Good: "none",
      Minor: "minor",
      Major: "major",
      // Not applicable: the driver says the question doesn't apply. Not a defect and not Good.
      Na: "na",
    },
    // What picking the option says about the item, apart from its severity.
    // Missing means neither.
    Meaning: {
      Ok: "ok",
      Na: "na",
    },
    // What sort of row the option is in a defect list. Missing means an ordinary answer.
    Kind: {
      // Text only, such as "When use of lamp is required:" - nothing to tick.
      Label: "label",
      // A free-text remark field, such as Minor Notes or User Major.
      Remark: "remark",
    },
  }
  
  constructor(domain, data) {
    super(domain, data, CheckListItemOption.MODEL_NAME);
  }

  fields() {
    return CheckListItemOption.Fields;
  }
  
  find() {
    var result = this.domain.checklists().items().options().findById(this.id());
    if (result.id() !== this.id()) {
      result = this;
    }
    
    return result;
  }

  index() {
    return this.getInt(CheckListItemOption.Fields.Index);
  }
  
  withIndex(index) {
    this.put(CheckListItemOption.Fields.Index, index);
    return this;
  }
  
  name() {
    return this.getString(CheckListItemOption.Fields.Name);
  }
  
  withName(name) {
    this.put(CheckListItemOption.Fields.Name, name);
    return this;
  }
  
  company() {
    var data = this.get(CheckListItemOption.Fields.Company);
    var company = new Company(this.domain, data);
    return company;
  }
  
  withCompany(company) {
    this.put(CheckListItemOption.Fields.Company, company);
    return this;
  }
  
  withCategoryGood() {
    return this.withCategory(CheckListItemOption.Values.Category.Good)
  }

  withCategoryMinor() {
    return this.withCategory(CheckListItemOption.Values.Category.Minor)
  }

  withCategoryMajor() {
    return this.withCategory(CheckListItemOption.Values.Category.Major)
  }

  withCategoryNa() {
    return this.withCategory(CheckListItemOption.Values.Category.Na);
  }

  isCategoryNa() {
    return StringUtils.eic(CheckListItemOption.Values.Category.Na, this.category());
  }

  withCategory(category) {
    this.put(CheckListItemOption.Fields.Category, category);
    return this;
  }
  
  category() {
    var category = this.getString(CheckListItemOption.Fields.Category);
    if (StringUtils.isEmpty(category)) {
      category = CheckListItemOption.Values.Category.Good;
    }
    return category;
  }
  
  isGood() {
    return this.isCategoryGood();
  }

  isOk() {
    return this.isGood();
  }
  
  isCategoryGood() {
    var cat = this.category();
    if (StringUtils.eic(CheckListItemOption.Values.Category.Good, cat) || StringUtils.isEmpty(cat)) {
      return true;
    }
    return false;
  }

  isMinor() {
    return this.isCategoryMinor();
  }

  isCategoryMinor() {
    var cat = this.category();
    if (StringUtils.eic(CheckListItemOption.Values.Category.Minor, cat)) {
      return true;
    }
    return false;
  }

  isMajor() {
    return this.isCategoryMajor();
  }

  isCategoryMajor() {
    var cat = this.category();
    if (StringUtils.eic(CheckListItemOption.Values.Category.Major, cat)) {
      return true;
    }
    return false;
  }

  // Notes describing the answer more fully, as a schedule does under "a) Front lamps".
  // Each note's value is Markdown; they show in position order. An empty map when the
  // answer has none, including when a read returns the property as null.
  noteMap() {
    return new NoteMap(this.domain, this.get(CheckListItemOption.Fields.NoteMap));
  }
  
  withNoteMap(noteMap) {
    this.put(CheckListItemOption.Fields.NoteMap, noteMap);
    return this;
  }
  
  // The notes in position order.
  noteList() {
    return this.noteMap().sortByPosition();
  }
  
  // Whether any note says anything; blank notes count as none.
  hasNotes() {
    return this.noteMap().hasValues();
  }
  
  meaning() {
    return this.getString(CheckListItemOption.Fields.Meaning);
  }
  
  withMeaning(meaning) {
    this.put(CheckListItemOption.Fields.Meaning, meaning);
    return this;
  }
  
  withMeaningOk() {
    return this.withMeaning(CheckListItemOption.Values.Meaning.Ok);
  }
  
  withMeaningNa() {
    return this.withMeaning(CheckListItemOption.Values.Meaning.Na);
  }
  
  // Both meaning checks match exactly, as the server's SdChecklistItemOption does.
  // Not applicable. Decided by the stored meaning alone - never by the option's
  // record id, since the common OK and N/A options are shared between items.
  // Marked by its meaning, or by the "na" category the phones and the PDF already read.
  isNa() {
    return this.meaning() === CheckListItemOption.Values.Meaning.Na || this.isCategoryNa();
  }
  
  // Marked as the item's OK answer. Decided by the stored meaning alone, unlike
  // isOk(), which also holds for any option without a defect.
  isOkByMeaning() {
    return this.meaning() === CheckListItemOption.Values.Meaning.Ok;
  }
  
  kind() {
    return this.getString(CheckListItemOption.Fields.Kind);
  }
  
  withKind(kind) {
    this.put(CheckListItemOption.Fields.Kind, kind);
    return this;
  }
  
  // Both kind checks match the stored kind exactly, never the option's name.
  isLabel() {
    return this.kind() === CheckListItemOption.Values.Kind.Label;
  }
  
  isRemark() {
    return this.kind() === CheckListItemOption.Values.Kind.Remark;
  }
  
  // The letter printed before the option on a schedule, such as "a".
  position() {
    return this.getString(CheckListItemOption.Fields.Position);
  }
  
  withPosition(position) {
    this.put(CheckListItemOption.Fields.Position, position);
    return this;
  }
  
  copy() {
    var data = this._copy();
    return new CheckListItemOption(this.domain, data);
  }
}